import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "@/hooks/use-debounce";
import { cn } from "@/lib/utils";

declare global {
  interface Window {
    __lastClickWasModified?: boolean;
  }
}

export interface AsyncSelectProps<T> {
  /** Async function to fetch options */
  fetcher: (
    query?: string,
    page?: number,
  ) => Promise<{ results: T[]; hasMore: boolean }>;
  /** Preload all data ahead of time */
  preload?: boolean;
  /** Function to render each option */
  renderOption: (option: T, isSelected?: boolean) => React.ReactNode;
  /** Function to get the value from an option */
  getOptionValue: (option: T) => string;
  /** Function to get the display value for the selected option */
  getDisplayValue: (option: T) => React.ReactNode;
  /** Custom not found message */
  notFound?: React.ReactNode;
  /** Custom loading skeleton */
  loadingSkeleton?: React.ReactNode;
  /** Currently selected value */
  value: string;
  /** Currently selected option (if available) */
  selectedOption?: T;
  /** Callback when selection changes */
  onChange: (value: string, option: T | null) => void;
  /** Label for the select field */
  label: string;
  /** Placeholder text when no selection */
  placeholder?: string;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Disable the entire select */
  disabled?: boolean;
  /** Custom width for the popover */
  width?: string | number;
  /** Custom class names */
  className?: string;
  /** Custom trigger button class names */
  triggerClassName?: string;
  /** Custom no results message */
  noResultsMessage?: string;
  /** Allow clearing the selection */
  clearable?: boolean;
  /** Threshold for when to load more items (0-1, default 0.8) */
  loadThreshold?: number;
  /** Whether the search is currently in progress */
  isSearching?: boolean;
}

export function AsyncSelect<T>({
  fetcher,
  preload,
  renderOption,
  getOptionValue,
  getDisplayValue,
  notFound,
  loadingSkeleton,
  label,
  placeholder = "Select...",
  searchPlaceholder,
  value,
  selectedOption: initialSelectedOption,
  onChange,
  disabled = false,
  className,
  triggerClassName,
  noResultsMessage,
  clearable = true,
  loadThreshold = 0.5,
}: AsyncSelectProps<T>) {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedValue, setSelectedValue] = useState(value);
  const [selectedOption, setSelectedOption] = useState<T | null>(
    initialSelectedOption ?? null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, preload ? 0 : 300);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Keep a stable reference to fetcher to avoid effect loops when its identity changes
  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  useEffect(() => {
    setMounted(true);
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    setSelectedOption(initialSelectedOption ?? null);
  }, [initialSelectedOption]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const { results, hasMore } = await fetcherRef.current(
          debouncedSearchTerm,
          1,
        );
        // Only update options if we have results or if this is the initial load
        if (results.length > 0 || options.length === 0) {
          setOptions(results);
        }
        setHasMore(hasMore);
        setPage(1);
        // If we have a selected value but no selected option, try to find it in the new data
        if (selectedValue && !selectedOption) {
          const option = results.find(
            (opt) => getOptionValue(opt) === selectedValue,
          );
          if (option) {
            setSelectedOption(option);
          }
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch options",
        );
      } finally {
        setLoading(false);
      }
    };

    if (!mounted) {
      fetchOptions();
    } else if (!preload && debouncedSearchTerm) {
      fetchOptions();
    } else if (preload || open) {
      fetchOptions();
    }
  }, [
    debouncedSearchTerm,
    mounted,
    preload,
    open,
    selectedValue,
    getOptionValue,
    options.length,
    selectedOption,
    // intentionally exclude selectedOption and fetcher to avoid refetch loops
  ]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const { results, hasMore: moreResults } = await fetcherRef.current(
        debouncedSearchTerm,
        nextPage,
      );
      setOptions((prev) => [...prev, ...results]);
      setHasMore(moreResults);
      setPage(nextPage);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load more options",
      );
    } finally {
      setLoadingMore(false);
    }
  }, [debouncedSearchTerm, page, hasMore, loadingMore]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight * (1 + loadThreshold)) {
        loadMore();
      }
    },
    [loadMore, loadThreshold],
  );

  const handleSelect = useCallback(
    (currentValue: string) => {
      const newValue =
        clearable && currentValue === selectedValue ? "" : currentValue;
      setSelectedValue(newValue);

      // Find the option in the current options list
      const option =
        options.find((opt) => getOptionValue(opt) === newValue) ?? null;

      // If we found the option, update immediately
      if (option) {
        setSelectedOption(option);
      }

      onChange(newValue, option);
      setOpen(false);
    },
    [selectedValue, onChange, clearable, options, getOptionValue],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between gap-6 max-w-full min-w-[300px] overflow-hidden",
            disabled && "opacity-50 cursor-not-allowed",
            triggerClassName,
          )}
          disabled={disabled}
        >
          {selectedOption ? getDisplayValue(selectedOption) : placeholder}
          <ChevronDownIcon className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "p-0 w-[420px] max-w-[90vw] max-h-[70vh] overflow-hidden",
          className,
        )}
        align="start"
        sideOffset={4}
      >
        <Command
          ref={listRef}
          className={cn(
            "flex h-full w-full flex-col overflow-hidden rounded-lg corner-smooth-lg bg-popover text-popover-foreground",
            className,
          )}
          shouldFilter={false} // Disable Command's built-in filtering since we filter server-side
        >
          <CommandInput
            placeholder={
              searchPlaceholder ?? `Search ${label.toLowerCase()}...`
            }
            value={searchTerm}
            onValueChange={setSearchTerm}
            data-testid="async-select-input"
          />
          <CommandList
            onScroll={handleScroll}
            className="overflow-y-auto max-h-[300px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-md"
          >
            {error && (
              <div
                key="error-message"
                className="p-4 text-destructive text-center"
              >
                {error}
              </div>
            )}
            {loading && options.length === 0 && (
              <CommandGroup>
                {loadingSkeleton || <DefaultLoadingSkeleton />}
              </CommandGroup>
            )}
            {!loading &&
              !error &&
              options.length === 0 &&
              (notFound || (
                <CommandEmpty>
                  {noResultsMessage ?? `No ${label.toLowerCase()} found.`}
                </CommandEmpty>
              ))}
            {loading && options.length > 0 && (
              <div className="py-2 text-center text-sm text-muted-foreground">
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Updating results...</span>
                </div>
              </div>
            )}
            {options.length > 0 && (
              <CommandGroup>
                {options.map((option, index) => {
                  const optionValue = getOptionValue(option);
                  return (
                    <CommandItem
                      key={`${index}-${getOptionValue(option)}`}
                      value={optionValue}
                      onSelect={(value) => {
                        // Don't handle selection if it was a command/ctrl+click
                        if (window.__lastClickWasModified) {
                          window.__lastClickWasModified = false;
                          return;
                        }
                        handleSelect(value);
                      }}
                      className="flex items-center gap-2 rounded-lg cursor-pointer group"
                      data-testid={`async-select-option-${optionValue}`}
                      onMouseDown={(e) => {
                        // Handle command/ctrl+click
                        if (e.metaKey || e.ctrlKey) {
                          window.__lastClickWasModified = true;
                          const url = `${window.location.pathname}?accountId=${optionValue}`;
                          window.open(url, "_blank");
                        }
                      }}
                    >
                      {renderOption(option)}
                      <Check
                        className={cn(
                          "ml-auto h-3 w-3",
                          selectedValue === optionValue
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {loadingMore && (
              <div
                key="loading-more"
                className="py-2 text-center text-sm text-muted-foreground"
              >
                Loading more...
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DefaultLoadingSkeleton() {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <CommandItem key={`skeleton-${i}`} disabled>
          <div className="flex items-center gap-2 w-full">
            <div className="h-6 w-6 rounded-lg corner-smooth-lg animate-pulse bg-muted" />
            <div className="flex flex-col flex-1 gap-1">
              <div className="h-4 w-24 animate-pulse bg-muted rounded-lg corner-smooth-lg" />
              <div className="h-3 w-16 animate-pulse bg-muted rounded-lg corner-smooth-lg" />
            </div>
          </div>
        </CommandItem>
      ))}
    </>
  );
}
